import { connect, ConnectedProps } from 'react-redux';
import { reset } from 'redux-form';

import { AddAllowedHostFormData } from 'dashboard/forms/FormAddAllowedHost';
import { AddAllowedUriSchemeFormData } from 'dashboard/forms/FormAddAllowedUriScheme';
import { OrganizationSubdomainFormData } from 'dashboard/forms/FormOrganizationSubdomain';
import {
  FORM_ADD_ALLOWED_HOST,
  FORM_ADD_ALLOWED_URI_SCHEME,
} from 'dashboard/lib/constants/formNames';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { updateOrganization } from 'dashboard/redux/thunk-actions/organization';
import DashboardInquiriesDomainManagerView from 'dashboard/views/DashboardInquiriesDomainManagerView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardInquiriesDomainManagerRoute = (props: PropsFromRedux) => {
  const { error: updateOrganizationSubdomainError, callback: updateOrganizationSubdomain } =
    useApiCallback(
      async ({ subdomain }: OrganizationSubdomainFormData) => {
        props.clearMessages();
        await props.updateOrganization({ subdomain });
        props.showMessage('Subdomain successfully updated.', MessageType.Success);
      },
      [props.organization],
    );

  useApiErrorHandler({
    error: updateOrganizationSubdomainError,
    onError: (e) => {
      if (isPermissionsError(e)) {
        props.showMessage(permissionsErrorMessage(e), MessageType.Failure);
      } else {
        switch (e.status) {
          case 401:
            props.showMessage("You don't have permissions to make this change.");
            break;
          default:
            props.showMessage('Failed to update subdomain. Please try again.');
        }
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const { error: addAllowedHostError, callback: addAllowedHost } = useApiCallback(
    async ({ host }: AddAllowedHostFormData) => {
      props.clearMessages();
      const currentAllowedHosts = props.organization.attributes['allowed-hosts'];
      await props.updateOrganization({ 'allowed-hosts': currentAllowedHosts.concat(host) });
      props.showMessage('Domain successfully added.', MessageType.Success);
      props.reset(FORM_ADD_ALLOWED_HOST);
    },
    [props.organization],
  );

  useApiErrorHandler({
    error: addAllowedHostError,
    onError: (e) => {
      if (isPermissionsError(e)) {
        props.showMessage(permissionsErrorMessage(e), MessageType.Failure);
      } else {
        switch (e.status) {
          case 401:
            props.showMessage("You don't have permissions to make this change.");
            break;
          default:
            props.showMessage('Failed to update allowed hosts. Please try again.');
        }
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const { error: removeAllowedHostError, callback: removeAllowedHost } = useApiCallback(
    async (hostIndex: number) => {
      props.clearMessages();
      const currentAllowedHosts = props.organization.attributes['allowed-hosts'];
      await props.updateOrganization({
        'allowed-hosts': [
          ...currentAllowedHosts.slice(0, hostIndex),
          ...currentAllowedHosts.slice(hostIndex + 1),
        ],
      });
      props.showMessage('Domain successfully removed', MessageType.Success);
    },
    [props.organization],
  );

  useApiErrorHandler({
    error: removeAllowedHostError,
    onError: (e) => {
      if (isPermissionsError(e)) {
        props.showMessage(permissionsErrorMessage(e), MessageType.Failure);
      } else {
        switch (e.status) {
          case 401:
            props.showMessage("You don't have permissions to make this change.");
            break;
          default:
            props.showMessage('Failed to remove domain. Please try again.');
        }
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const { error: addAllowedUriSchemeError, callback: addAllowedUriScheme } = useApiCallback(
    async ({ uriScheme }: AddAllowedUriSchemeFormData) => {
      props.clearMessages();
      const currentAllowedUriSchemes = props.organization.attributes['allowed-uri-schemes'];
      await props.updateOrganization({
        'allowed-uri-schemes': currentAllowedUriSchemes.concat(uriScheme),
      });
      props.showMessage('URI scheme successfully added.', MessageType.Success);
      props.reset(FORM_ADD_ALLOWED_URI_SCHEME);
    },
    [props.organization],
  );

  useApiErrorHandler({
    error: addAllowedUriSchemeError,
    onError: (e) => {
      if (isPermissionsError(e)) {
        props.showMessage(permissionsErrorMessage(e), MessageType.Failure);
      } else {
        switch (e.status) {
          case 401:
            props.showMessage("You don't have permissions to make this change.");
            break;
          default:
            props.showMessage('Failed to update URI scheme. Please try again.');
        }
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const { error: removeAllowedUriSchemeError, callback: removeAllowedUriScheme } = useApiCallback(
    async (uriSchemeIndex: number) => {
      props.clearMessages();
      const currentAllowedUriSchemes = props.organization.attributes['allowed-uri-schemes'];
      await props.updateOrganization({
        'allowed-uri-schemes': [
          ...currentAllowedUriSchemes.slice(0, uriSchemeIndex),
          ...currentAllowedUriSchemes.slice(uriSchemeIndex + 1),
        ],
      });
      props.showMessage('URI scheme successfully removed', MessageType.Success);
    },
    [props.organization],
  );

  useApiErrorHandler({
    error: removeAllowedUriSchemeError,
    onError: (e) => {
      if (isPermissionsError(e)) {
        props.showMessage(permissionsErrorMessage(e), MessageType.Failure);
      } else {
        switch (e.status) {
          case 401:
            props.showMessage("You don't have permissions to make this change.");
            break;
          default:
            props.showMessage('Failed to remove URI scheme. Please try again.');
        }
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  return (
    <DashboardInquiriesDomainManagerView
      addAllowedHost={addAllowedHost}
      addAllowedUriScheme={addAllowedUriScheme}
      allowedHosts={props.organization.attributes['allowed-hosts']}
      allowedUriSchemes={props.organization.attributes['allowed-uri-schemes']}
      removeAllowedHost={removeAllowedHost}
      removeAllowedUriScheme={removeAllowedUriScheme}
      subdomain={props.organization.attributes.subdomain}
      updateOrganizationSubdomain={updateOrganizationSubdomain}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    organization: state.organization.organization,
  };
};

const mapDispatchToProps = {
  clearMessages,
  reset,
  showMessage,
  updateOrganization,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiriesDomainManagerRoute);
